// src/components/tiptap/extensions/AttachmentNode.jsx
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../config/supabase';
import * as Fi from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon.jsx';

// Accept images/PDFs inline; others become file tiles
const ACCEPTED_INLINE = ['image/', 'application/pdf'];

// Per-page cache to avoid re-signing attachments repeatedly
const attachmentSignedUrlCache = new Map();
// key -> { signedUrl, downloadUrl, previewUrl, expiresAt }

// Small helper to avoid accidental editor selection while interacting with the node
const isFormControl = (el) =>
  el && (
    el.tagName === 'SELECT' ||
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.isContentEditable
  );

export const AttachmentNode = Node.create({
  name: 'attachment',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      id: { default: null },                 // lesson_note_attachments.id
      noteBlockId: { default: null },        // UUID anchor to row
      lessonNoteId: { default: null },
      schoolId: { default: null },
      studentId: { default: null },
      studentMasterId: { default: null },

      // file metadata
      fileName: { default: null },
      mimeType: { default: null },
      byteSize: { default: null },
      storageKey: { default: null },         // storage path
      previewStorageKey: { default: null },  // preview path

      // runtime state
      status: { default: 'idle' },           // idle | picking | uploading | saved | error
      signedUrl: { default: null },          // view
      previewUrl: { default: null },
      downloadUrl: { default: null },        // forced download (attachment)
    };
  },

  parseHTML() { return [{ tag: 'attachment-block' }]; },
  renderHTML({ HTMLAttributes }) { return ['attachment-block', mergeAttributes(HTMLAttributes)]; },
  addNodeView() { return ReactNodeViewRenderer(AttachmentView); },
});

function AttachmentView({ node, updateAttributes, editor, getPos, deleteNode }) {
  const attrs = node.attrs;
  const [deleting, setDeleting] = useState(false);
  const [dragDepth, setDragDepth] = useState(0);  // robust drag enter/leave
  const inputRef = useRef(null);
  const storage = editor?.storage?.mms || {};
  const canEdit = !!storage?.userId && !!editor?.isEditable; // teachers only
  const hasFile = !!(attrs.storageKey || attrs.id);
  const isSaved = hasFile; // source of truth

  // hydrate IDs from editor.storage (same pattern as AudioRecorder)
  useEffect(() => {
    const patch = {};
    const s = editor?.storage?.mms || {};
    if (!attrs.noteBlockId) patch.noteBlockId = crypto.randomUUID?.() ?? String(Date.now());
    if (!attrs.lessonNoteId && s.lessonNoteId) patch.lessonNoteId = s.lessonNoteId;
    if (!attrs.schoolId && s.schoolId) patch.schoolId = s.schoolId;
    if (!attrs.studentId && s.studentId) patch.studentId = s.studentId;
    if (!attrs.studentMasterId && s.masterStudentId) patch.studentMasterId = s.masterStudentId;
    if (Object.keys(patch).length) updateAttributes(patch);
  }, [
    editor,
    attrs.noteBlockId,
    attrs.lessonNoteId,
    attrs.schoolId,
    attrs.studentId,
    attrs.studentMasterId,
    updateAttributes
  ]);

  // signed URLs (view + download + preview) — cached + gated
useEffect(() => {
  let cancelled = false;

  (async () => {
    if (!attrs.storageKey && !attrs.previewStorageKey) {
      updateAttributes({ signedUrl: null, downloadUrl: null, previewUrl: null });
      return;
    }

    const ttlSeconds = 60 * 60 * 24 * 7;

    const cacheKey = [
      attrs.storageKey,
      attrs.previewStorageKey,
      attrs.fileName,
    ].filter(Boolean).join('|');

    const cached = attachmentSignedUrlCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now() + 30_000) {
      if (!cancelled) {
        updateAttributes({
          signedUrl: cached.signedUrl,
          downloadUrl: cached.downloadUrl,
          previewUrl: cached.previewUrl,
        });
      }
      return;
    }

    // View URL
    const viewRes = attrs.storageKey
      ? await supabase.storage
          .from('note-attachments')
          .createSignedUrl(attrs.storageKey, ttlSeconds)
      : { data: null };

    // Download URL (forces Content-Disposition)
    const downloadRes = attrs.storageKey
      ? await supabase.storage
          .from('note-attachments')
          .createSignedUrl(attrs.storageKey, ttlSeconds, {
            download: attrs.fileName || 'attachment',
          })
      : { data: null };

    // Preview URL — ONLY if inline display is possible
    const isInline =
      !!attrs.mimeType &&
      ACCEPTED_INLINE.some(p => attrs.mimeType.startsWith(p));

    const previewRes =
      isInline && attrs.previewStorageKey
        ? await supabase.storage
            .from('note-attachments')
            .createSignedUrl(attrs.previewStorageKey, ttlSeconds)
        : { data: null };

    const next = {
      signedUrl: viewRes.data?.signedUrl || null,
      downloadUrl: downloadRes.data?.signedUrl || null,
      previewUrl: previewRes.data?.signedUrl || null,
    };

    if (!cancelled) updateAttributes(next);

    attachmentSignedUrlCache.set(cacheKey, {
      ...next,
      expiresAt: Date.now() + (ttlSeconds - 60) * 1000,
    });
  })();

  return () => { cancelled = true; };
}, [attrs.storageKey, attrs.previewStorageKey, attrs.fileName, attrs.mimeType]);

  async function onPick(e) {
    if (!canEdit) return;
    const file = e?.target?.files?.[0];
    if (!file) return;
    await handleFile(file);
    // reset input so selecting the same file re-triggers
    if (inputRef.current) inputRef.current.value = '';
  }

  function onDragEnter(e) {
    e.preventDefault();
    setDragDepth((d) => d + 1);
  }
  function onDragLeave(e) {
    e.preventDefault();
    setDragDepth((d) => Math.max(0, d - 1));
  }
  function onDragOver(e) {
    // prevent default so drop works and so PM doesn't show its dropcursor line
    e.preventDefault();
  }
  async function onDrop(e) {
    e.preventDefault();
    setDragDepth(0);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await handleFile(file);
  }

  async function handleFile(file) {
    if (!canEdit) return;
    try {
      // basic context guards to avoid server 400s
      const s = editor?.storage?.mms || {};
      const lessonNoteId = attrs.lessonNoteId ?? s.lessonNoteId ?? null;
      const schoolId = attrs.schoolId ?? s.schoolId ?? null;
      if (!lessonNoteId) throw new Error('Missing lessonNoteId (Attachment needs note context).');
      if (!schoolId) throw new Error('Missing schoolId (Attachment needs school context).');

      updateAttributes({ status: 'uploading' });

      const ext = extFromNameOrType(file);
      const { data: uidRes, error: uidErr } = await supabase.rpc('current_user_id');
      if (uidErr || !uidRes) throw new Error('Cannot resolve user id');

      const nbid = attrs.noteBlockId || crypto.randomUUID?.() || String(Date.now());
      if (!attrs.noteBlockId) updateAttributes({ noteBlockId: nbid });

      const storageKey = `${uidRes}/${lessonNoteId}/${nbid}.${ext}`;

      // Prepare preview (image resize or pdf page 1)
      let previewBlob = null;
      if (file.type.startsWith('image/')) {
        previewBlob = await generateImagePreview(file, 1600);
      } else if (file.type === 'application/pdf') {
        try { previewBlob = await generatePdfPreview(file); } catch { /* optional */ }
      }
      const previewKey = previewBlob
        ? `${uidRes}/${lessonNoteId}/${nbid}.preview.jpg`
        : null;

      // Upload original (upsert)
      const up1 = await supabase.storage.from('note-attachments').upload(storageKey, file, {
        cacheControl: '3600',
        contentType: file.type || 'application/octet-stream',
        upsert: true,
      });
      if (up1.error) throw up1.error;

      // Upload preview (optional)
      if (previewBlob) {
        const up2 = await supabase.storage.from('note-attachments').upload(previewKey, previewBlob, {
          cacheControl: '3600',
          contentType: 'image/jpeg',
          upsert: true,
        });
        if (up2.error) console.warn('Preview upload failed', up2.error);
      }

      // Persist row via RPC
      const { data: row, error: rpcErr } = await supabase.rpc('upsert_lesson_note_attachment', {
        p_school_id: schoolId,
        p_lesson_note_id: lessonNoteId,
        p_note_block_id: nbid,
        p_file_name: file.name,
        p_mime_type: file.type || 'application/octet-stream',
        p_byte_size: file.size,
        p_storage_key: storageKey,
        p_preview_storage_key: previewKey,
        p_image_width: null, p_image_height: null, p_page_count: null,
        p_student_id: attrs.studentId ?? s.studentId ?? null,
        p_student_master_id: attrs.studentMasterId ?? s.masterStudentId ?? null,
      });
      if (rpcErr) throw rpcErr;

      // Get signed URLs (view + download + preview)
      const viewRes = await supabase.storage
        .from('note-attachments')
        .createSignedUrl(storageKey, 60 * 60 * 24 * 7);

      const downloadRes = await supabase.storage
        .from('note-attachments')
        .createSignedUrl(storageKey, 60 * 60 * 24 * 7, {
          download: file.name || 'attachment',
        });

      const previewRes = previewKey
        ? await supabase.storage.from('note-attachments').createSignedUrl(previewKey, 60 * 60 * 24 * 7)
        : { data: null };

      updateAttributes({
        id: row.id,
        fileName: row.file_name,
        mimeType: row.mime_type,
        byteSize: row.byte_size,
        storageKey: row.storage_key,
        previewStorageKey: row.preview_storage_key,
        signedUrl: viewRes.data?.signedUrl || null,
        downloadUrl: downloadRes.data?.signedUrl || null,
        previewUrl: previewRes.data?.signedUrl || null,
        status: 'saved',
      });
    } catch (e) {
      console.error(e);
      updateAttributes({ status: 'error' });
      alert(e?.message || 'Upload failed. Please try again.');
    }
  }

  async function onDelete() {
    if (!confirm('Delete this attachment?')) return;
    setDeleting(true);
    try {
      await supabase.rpc('delete_lesson_note_attachment', {
        p_id: attrs.id,
        p_lesson_note_id: attrs.lessonNoteId,
        p_note_block_id: attrs.noteBlockId,
      });

      // best-effort storage cleanup
      const keys = [attrs.storageKey, attrs.previewStorageKey].filter(Boolean);
      if (keys.length) await supabase.storage.from('note-attachments').remove(keys);

      const pos = typeof getPos === 'function' ? getPos() : null;
      if (typeof pos === 'number') {
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).insertContent({ type: 'paragraph' }).run();
      } else {
        deleteNode?.();
      }
    } catch (e) {
      console.error('Delete failed', e);
      alert('Sorry—could not delete this attachment.');
    } finally {
      setDeleting(false);
    }
  }

  const isImage = (attrs.mimeType || '').startsWith('image/');
  const isPdf = (attrs.mimeType === 'application/pdf') || (attrs.fileName || '').toLowerCase().endsWith('.pdf');
  const canInlineView = isImage || isPdf;

  const isInlineDisplay = useMemo(() => {
    return !!attrs.mimeType && (ACCEPTED_INLINE.some(p => attrs.mimeType.startsWith(p)));
  }, [attrs.mimeType]);

  // drag highlight classes
  const dragHighlight = dragDepth > 0 ? 'ring-2 ring-orange-400 ring-offset-2' : '';

  return (
    <NodeViewWrapper
      as="div"
      contentEditable={false}          // prevents iPad keyboard and PM selection while interacting
      role="group"
      tabIndex={-1}
      className={`relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200 ${dragHighlight}`}
      onMouseDownCapture={(e) => { if (!isFormControl(e.target)) e.preventDefault(); e.stopPropagation(); }}
      onTouchStartCapture={(e) => { if (!isFormControl(e.target)) e.preventDefault(); e.stopPropagation(); }}

      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={Fi.FiPaperclip} className="w-6 h-6" />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">Attachment</div>
            <div className="text-sm text-slate-500">Drop a file here or choose from your computer.</div>
          </div>
        </div>
        {canEdit && (attrs.id || attrs.storageKey) && (
        <button
            className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
            onClick={onDelete}
            disabled={deleting}
            title="Delete attachment"
            aria-label="Delete attachment"
        >
            <SafeIcon icon={Fi.FiTrash2} className="w-5 h-5" />
        </button>
        )}
      </div>

      {!isSaved ? (
        <div className="flex flex-col gap-2">
          <div
            className="rounded-xl border border-dashed border-slate-300 bg-white p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => { if (canEdit) inputRef.current?.click(); }}
          >
            <div className="text-sm text-slate-600">
              <span className="font-semibold">Click to choose</span> or drag & drop a file here
            </div>
            <div className="text-xs text-slate-400 mt-1">Images, PDFs, and common docs</div>
          </div>
          {canEdit && (
            <input ref={inputRef} type="file" className="hidden" onChange={onPick} />
           )}
          {attrs.status === 'uploading' && (
            <div className="text-sm text-slate-600">Uploading…</div>
          )}
          {attrs.status === 'error' && (
            <div className="text-sm text-red-600">Something went wrong. Please try again.</div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Preview area */}
          {isInlineDisplay ? (
            <>
              {isImage && attrs.previewUrl && (
                <img
                  src={attrs.previewUrl}
                  alt={attrs.fileName || 'Image'}
                  className="rounded-lg max-h-[420px] object-contain bg-white"
                />
              )}
              {isPdf && (
                <div className="flex items-center gap-3">
                  {attrs.previewUrl && (
                    <img src={attrs.previewUrl} alt="PDF preview" className="h-24 w-auto rounded-md border bg-white" />
                  )}
                  <div className="text-sm text-slate-700 truncate">{attrs.fileName}</div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-3 rounded-lg border bg-white p-3">
              <SafeIcon icon={Fi.FiFile} className="text-slate-400" />
              <div className="min-w-0">
                <div className="text-sm text-slate-800 truncate">{attrs.fileName}</div>
                <div className="text-xs text-slate-500 truncate">{attrs.mimeType} · {formatBytes(attrs.byteSize)}</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Only show "View" for images & PDFs */}
            {attrs.signedUrl && (
              <a
                href={attrs.signedUrl}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-slate-300 bg-white hover:bg-slate-100 transition-colors"
              >
                View
              </a>
            )}

            {/* Always show "Download" when available; orange to match Memory Match */}
            {attrs.downloadUrl && (
              <a
                href={attrs.downloadUrl}
                download={attrs.fileName || 'attachment'}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 active:scale-95 transition-transform"
              >
                Download
              </a>
            )}
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
}

/* helpers */
function extFromNameOrType(file) {
  const nameExt = file.name.split('.').pop()?.toLowerCase();
  if (nameExt && nameExt.length <= 5) return nameExt;
  const t = (file.type || '').toLowerCase();
  if (t.includes('png')) return 'png';
  if (t.includes('jpeg') || t.includes('jpg')) return 'jpg';
  if (t.includes('gif')) return 'gif';
  if (t.includes('pdf')) return 'pdf';
  return 'bin';
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return '';
  const k = 1024, sizes = ['B','KB','MB','GB','TB'];
  const i = Math.floor(Math.log(n)/Math.log(k));
  return `${(n/Math.pow(k,i)).toFixed(1)} ${sizes[i]}`;
}

async function generateImagePreview(file, maxLongEdge = 1600) {
  try {
    // Try OffscreenCanvas first
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxLongEdge / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);

    if (typeof OffscreenCanvas !== 'undefined') {
      const c = new OffscreenCanvas(w, h);
      const ctx = c.getContext('2d');
      ctx.drawImage(bmp, 0, 0, w, h);
      return await c.convertToBlob({ type: 'image/jpeg', quality: 0.82 });
    }

    // Fallback to DOM canvas
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bmp, 0, 0, w, h);
    return await new Promise((res) => canvas.toBlob((b)=>res(b), 'image/jpeg', 0.82));
  } catch {
    return null;
  }
}

// Simple pdf.js-based first-page preview; optional
async function generatePdfPreview(file) {
  if (!window.pdfjsLib) return null;
  const buf = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  await page.render({ canvasContext: ctx, viewport }).promise;
  return await new Promise((res) => canvas.toBlob((b)=>res(b), 'image/jpeg', 0.82));
}

export default AttachmentNode;